import { PickedFile } from '@capawesome/capacitor-file-picker';
import { ProjectDoc } from 'common';
import { base64toBlob, blobToBase64, resizeImage } from '../../global/utils/data';

/**
 * Utility functions for project data formatting, parsing, and manipulation
 */
export class ProjectUtils {
  /**
   * Format address line 2 from project document
   */
  static getAddressLine2(project: ProjectDoc): string {
    return `${project.city}, ${project.state_code} ${project.zip}`;
  }

  /**
   * Get thumbnail URL from project document
   * Returns a blob URL if attachment exists, otherwise returns default placeholder
   */
  static getThumbnailUrl(project: ProjectDoc): string {
    if (!project._attachments || !project._attachments.hasOwnProperty('thumbnail') || !project._attachments['thumbnail'].data) {
      return '/assets/img/card-media.png';
    }

    const attachment = project._attachments['thumbnail'];
    const contentType = attachment.content_type;
    const blob = base64toBlob(attachment.data, contentType);
    return URL.createObjectURL(blob);
  }

  /**
   * Set thumbnail data on project document from a data URL string
   */
  static setThumbnailFromDataUrl(project: ProjectDoc, dataUrl: string): void {
    project._attachments = project._attachments || {};
    project._attachments['thumbnail'] = { content_type: 'image/unused', data: dataUrl };
  }

  /**
   * Process and set thumbnail from a picked file
   * Resizes image to 400x225 and converts to WebP format
   */
  static async setThumbnailFromFile(project: ProjectDoc, file: PickedFile): Promise<void> {
    if (file) {
      const blob = await resizeImage(file.blob, 400, 225, 'image/webp');
      const base64 = await blobToBase64(blob);
      project._attachments = project._attachments || {};
      project._attachments['thumbnail'] = { content_type: blob.type, data: base64 };
    }
  }

  /**
   * Check if project has a thumbnail attachment
   */
  static hasThumbnail(project: ProjectDoc): boolean {
    return !!(project._attachments && project._attachments['thumbnail'] && project._attachments['thumbnail'].data);
  }

  /**
   * Clear thumbnail from project document
   */
  static clearThumbnail(project: ProjectDoc): void {
    if (project._attachments && project._attachments['thumbnail']) {
      delete project._attachments['thumbnail'];
    }
  }
}
